// ============================================================
// ANSICHTSSACHE-AGENT – Tabelle
// ============================================================
// Die Tabelle traegt fast jede Listenseite. Zwei Aenderungen
// gegenueber der ersten Fassung, beide aus demselben Grund —
// weniger Linien, mehr Ruhe:
//
//   1. Zeilentrenner in `border-subtle` statt `border`. Bei
//      dreissig Zeilen untereinander addiert sich jede Linie zu
//      einem Raster, das lauter ist als der Inhalt.
//   2. Der Kopf steht auf `bg-secondary` und bleibt beim Scrollen
//      stehen — vorher war er weiss wie die Zeilen und damit beim
//      Scrollen nicht mehr als solcher zu erkennen.
// ============================================================

import { forwardRef } from "react";
import { cn } from "@/lib/utils";

/**
 * `viewportClassName` fasst den Kasten, IN dem die Tabelle
 * scrollt — nicht die Tabelle selbst.
 *
 * Gebraucht wird das, sobald eine Liste im Bild bleiben soll:
 * wer den Kasten auf eine feste Hoehe setzt, bekommt einen
 * stehenden Spaltenkopf und eine stehende Summenzeile geschenkt,
 * weil beide gegen genau diesen Kasten kleben (`sticky`). Ohne
 * die Moeglichkeit muesste die Seite die Tabelle nachbauen, nur
 * um einen zweiten Rahmen darum zu legen.
 *
 * `relative` aus demselben Grund wie am `Listenkoerper`: sonst
 * entkommen absolut gesetzte Elemente der Zeilen dem Kasten und
 * verlaengern die Seite.
 */
interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  viewportClassName?: string;
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, viewportClassName, ...props }, ref) => (
    <div className={cn("relative w-full overflow-x-auto", viewportClassName)}>
      <table
        ref={ref}
        // `h-full` gehoert zur Fuellzeile in `TableBody` (dort
        // steht, warum). Steht die Tabelle im normalen Fluss,
        // laeuft die Angabe ins Leere — 100 Prozent von einer
        // Hoehe, die selbst „auto" ist, ist keine Hoehe.
        className={cn("text-body-sm h-full w-full caption-bottom", className)}
        {...props}
      />
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    // Die einzige Linie, die bleibt: die unter dem Kopf. Sie
    // trennt Beschriftung von Daten, und das ist eine echte
    // Grenze.
    className={cn("border-border bg-bg-secondary sticky top-0 z-10 border-b", className)}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    // DIE FUELLZEILE — warum es sie gibt:
    //
    // Die Summenzeile klebt unten (`sticky bottom-0`), aber
    // kleben kann sie nur, wenn der Inhalt ueberlaeuft. Bei einer
    // kurzen Liste stand sie deshalb direkt unter der letzten
    // Zeile und wanderte mit jeder Zeile mehr nach unten — mal
    // hier, mal dort, und auf einem 945 Pixel hohen Schirm bei
    // genau neun Zeilen unter der Chat-Blase.
    //
    // Die leere Zeile am Ende nimmt den Rest der Hoehe auf und
    // schiebt die Summe ans untere Ende der Karte. Sie ist ein
    // Pseudo-Element und keine echte `<tr>`: so bleibt
    // `tr:last-child` weiterhin die letzte ECHTE Zeile (sonst
    // traegt sie ploetzlich wieder ihren Trennstrich), und keine
    // der 34 Listen muss etwas dazutun.
    //
    // Die Hoehen der Zeilen bleiben, wie sie sind — nachgemessen:
    // ohne Fuellzeile waechst eine 57er Zeile auf 66, weil die
    // Tabelle den Ueberschuss sonst auf alle Zeilen verteilt.
    className={cn(
      "animate-stagger [&_tr:last-child]:border-0",
      "after:[display:table-row] after:h-full after:content-['']",
      className,
    )}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableRow = forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        // Keine Zeilentrenner mehr. Was die Zeile zusammenhaelt,
        // ist ihre Hoehe und das Aufleuchten beim Zeigen — nicht
        // ein Strich darunter. Bei dreissig Zeilen spart das
        // dreissig Linien und liest sich ruhiger.
        //
        // `relative`, weil in jeder Liste die ganze Zeile oeffnet
        // (`components/liste/zeilenverweis.tsx`): die Flaeche des
        // Verweises misst an der Zeile. Fehlte es, deckte die
        // erste Zeile die ganze Tabelle zu.
        "duration-fast ease-standard relative transition-colors",
        "hover:bg-bg-secondary",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        // `tabellenzelle` statt `px-5`: der Innenabstand steht in
        // `styles/globals.css`, damit die `Listentabelle` die erste
        // und letzte Spalte an die Textkante der Karte ruecken kann
        // und jede Angabe der Seite trotzdem gewinnt.
        "tabellenzelle text-overline text-text-tertiary py-2.5 text-left font-medium whitespace-nowrap",
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = "TableHead";

const TableCell = forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn("tabellenzelle py-3.5 align-middle", className)} {...props} />
  ),
);
TableCell.displayName = "TableCell";

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
